import { InvalidUrlError, getPathname, putPathname, toUrlT } from '@execonline-inc/url';
import format from 'date-fns/format';
import { Task } from 'taskarian';
import { getTimezoneNames } from '../../../Utils/timezone';
import { AppyError } from '../../../Appy';
import { Avatar, avatarFrom } from '../../../Avatar';
import { MissingLinkError, findLink } from '../../../LinkyLinky';
import { ParticipantResource } from '../../../ParticipantsStore/Types';
import { Link } from '../../../Resource/Types';
import {
  GroupParticipantsResource,
  Participant,
} from '../../People/Group/GroupParticipantsStore/Types';
import { GroupOrParticipantResource } from './Invitations/InviteParticipantModal/Types';

export type InviteError = AppyError | InvalidUrlError | MissingLinkError;

export const allSelectedParticipantIds = (
  groupOrParticipants: ReadonlyArray<GroupOrParticipantResource>,
) => [
  ...filterParticipants(groupOrParticipants).map((p) => p.payload.id),
  ...filterGroups(groupOrParticipants).flatMap((p) => p.payload.allParticipantIds),
];

const createPath = (participantIds: number[]): string =>
  participantIds.map((number) => `participants_ids[]=${number}&`).join('');

export const buildLicenseInformationUrl =
  (participantIds: number[]) =>
  (link: Link): Task<InviteError, Link> =>
    toUrlT(link.href)
      .map((url) => putPathname(`${getPathname(url)}?${createPath(participantIds)}`, url))
      .map((url): Link => ({ ...link, href: url.href }));

const isParticipant = (
  groupOrParticipant: GroupOrParticipantResource,
): groupOrParticipant is ParticipantResource => groupOrParticipant.payload.kind === 'participant';

export const filterParticipants = (
  newGroupsAndParticipants: ReadonlyArray<GroupOrParticipantResource>,
): ReadonlyArray<ParticipantResource> => newGroupsAndParticipants.filter(isParticipant);

const isGroup = (
  groupOrParticipant: GroupOrParticipantResource,
): groupOrParticipant is GroupParticipantsResource =>
  groupOrParticipant.payload.kind === 'group-participants';

export const filterGroups = (
  newGroupsAndParticipants: ReadonlyArray<GroupOrParticipantResource>,
): ReadonlyArray<GroupParticipantsResource> => newGroupsAndParticipants.filter(isGroup);

export const getAvatar = (photo: ReadonlyArray<Link>, participant: Participant): Avatar =>
  avatarFrom(findLink('avatar', photo), participant.initials);

export const selectedParticipantCount = (
  newGroups: ReadonlyArray<GroupParticipantsResource>,
  newParticipants: ReadonlyArray<ParticipantResource>,
): number =>
  newParticipants.length +
  newGroups.reduce((acc, group) => acc + group.payload.totalParticipantsCount, 0);

export const dateToLocalDate = (date: Date) => format(date, 'yyyy-MM-dd');

export const dateToLocalTime = (time: Date) => format(time, 'HH:mm');

export const timezones = getTimezoneNames();

export const utcDateToLocalDate = (utcDate: Date): Date =>
  new Date(utcDate.getTime() + utcDate.getTimezoneOffset() * 60000);
